import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { formatLocalDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DollarSign, Search, X, Loader2, TrendingUp, RefreshCw } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useIsMobile } from "@/hooks/use-mobile";
import type { AccountsReceivable, Invoice } from "@shared/schema";

const formatCurrency = (amount: string | number | null | undefined): string => {
  const num = parseFloat(String(amount || "0"));
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

const formatDate = (date: string | Date | null | undefined): string =>
  formatLocalDate(date, { year: "numeric", month: "short", day: "numeric" });

export function AccountsReceivableTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [yearFilter, setYearFilter] = useState<string>(new Date().getFullYear().toString());
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();

  const { data: receivables = [], isLoading } = useQuery<AccountsReceivable[]>({
    queryKey: ["/api/billing/receivables"],
  });

  const { data: invoices = [] } = useQuery<Invoice[]>({
    queryKey: ["/api/invoices"],
  });

  const paidInvoicesCount = invoices.filter(inv => inv.status === "paid").length;

  const backfillMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/billing/receivables/backfill");
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to backfill");
      }
      return response.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/receivables"] });
      toast({
        title: "Backfill Complete",
        description: data.message || `Successfully backfilled ${data.backfilledCount} entries.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to backfill receivables.",
        variant: "destructive",
      });
    },
  });

  const invoiceMap = new Map(invoices.map((inv) => [inv.id, inv]));

  const filteredReceivables = receivables.filter((entry) => {
    const invoice = invoiceMap.get(entry.invoiceId || "");
    const matchesSearch =
      !searchQuery ||
      invoice?.accountName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      invoice?.invoiceNumber?.toLowerCase().includes(searchQuery.toLowerCase());

    if (yearFilter === "all") return matchesSearch;

    const entryDate = entry.paymentDate ? new Date(entry.paymentDate) : null;
    const matchesYear = entryDate && entryDate.getFullYear().toString() === yearFilter;
    return matchesSearch && matchesYear;
  });

  const totalReceived = filteredReceivables.reduce(
    (sum, e) => sum + parseFloat(e.paymentAmount || "0"),
    0
  );

  const thisMonthReceivables = filteredReceivables.filter((e) => {
    if (!e.paymentDate) return false;
    const date = new Date(e.paymentDate);
    const now = new Date();
    return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
  });

  const thisMonthTotal = thisMonthReceivables.reduce(
    (sum, e) => sum + parseFloat(e.paymentAmount || "0"),
    0
  );

  const lastMonthReceivables = filteredReceivables.filter((e) => {
    if (!e.paymentDate) return false;
    const date = new Date(e.paymentDate);
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return date.getMonth() === lastMonth.getMonth() && date.getFullYear() === lastMonth.getFullYear();
  });

  const lastMonthTotal = lastMonthReceivables.reduce(
    (sum, e) => sum + parseFloat(e.paymentAmount || "0"),
    0
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Received ({yearFilter})</CardTitle>
            <DollarSign className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-total-received">
              {formatCurrency(totalReceived)}
            </div>
            <p className="text-xs text-muted-foreground">
              {filteredReceivables.length} payments received
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">This Month</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600" data-testid="text-this-month">
              {formatCurrency(thisMonthTotal)}
            </div>
            <p className="text-xs text-muted-foreground">
              {thisMonthReceivables.length} payments this month
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Last Month</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-last-month">
              {formatCurrency(lastMonthTotal)}
            </div>
            <p className="text-xs text-muted-foreground">
              {lastMonthReceivables.length} payments last month
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle>Accounts Receivable</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Track payments received from clients
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-56"
                data-testid="input-search-receivables"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 no-default-hover-elevate"
                  onClick={() => setSearchQuery("")}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Select value={yearFilter} onValueChange={setYearFilter}>
              <SelectTrigger className="w-full sm:w-[120px]" data-testid="select-receivables-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                <SelectItem value="2026">2026</SelectItem>
                <SelectItem value="2025">2025</SelectItem>
                <SelectItem value="2024">2024</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {filteredReceivables.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No payments received yet</p>
              <p className="text-sm">
                Payments are automatically logged when invoices are marked as paid
              </p>
              {paidInvoicesCount > 0 && receivables.length === 0 && (
                <div className="mt-4">
                  <p className="text-sm mb-2">
                    Found {paidInvoicesCount} paid invoice(s) not yet in receivables.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => backfillMutation.mutate()}
                    disabled={backfillMutation.isPending}
                    data-testid="button-backfill-receivables"
                  >
                    {backfillMutation.isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Backfilling...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Sync Paid Invoices
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          ) : isMobile ? (
            <div className="space-y-2">
              {filteredReceivables.map((entry) => {
                const invoice = invoiceMap.get(entry.invoiceId || "");
                return (
                  <div key={entry.id} className="rounded-lg border border-border p-3" data-testid={`row-receivable-${entry.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{invoice?.accountName || "N/A"}</span>
                      <span className="text-green-600 font-bold">{formatCurrency(entry.paymentAmount)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span>#{invoice?.invoiceNumber || "N/A"}</span>
                      <span>·</span>
                      <span>{formatDate(entry.paymentDate)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReceivables.map((entry) => {
                    const invoice = invoiceMap.get(entry.invoiceId || "");
                    return (
                      <TableRow key={entry.id} data-testid={`row-receivable-${entry.id}`}>
                        <TableCell className="font-medium">
                          {invoice?.invoiceNumber || "N/A"}
                        </TableCell>
                        <TableCell>{invoice?.accountName || "N/A"}</TableCell>
                        <TableCell className="text-green-600 font-medium">
                          {formatCurrency(entry.paymentAmount)}
                        </TableCell>
                        <TableCell>{formatDate(entry.paymentDate)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
